import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import Close from "@mui/icons-material/Close";
import { Drawer } from "@mui/material";

import ErrorBoundary from "orion-components/ErrorBoundary";
import { getDir } from "orion-components/i18n/Config/selector";
import { contextPanelState } from "orion-components/ContextPanel/Selectors";
import { setLocalAppState, setMapOffset, setAppState, updatePersistedState } from "orion-components/AppState/Actions";

import SettingsMenu from "./Notifications/components/SettingsMenu";
import FilterSettings from "./Notifications/components/FilterSettings";
import * as actionCreators from "./actions";

import $ from "jquery";

const propTypes = {
	open: PropTypes.bool,
	dockTitle: PropTypes.string,
	notifications: PropTypes.array,
	bottomAlertListEnabled: PropTypes.bool,
	panelActions: PropTypes.node,
	children: PropTypes.node
};

const defaultProps = {
	notifications: []
};

const BottomDock = ({
	open,
	dockTitle,
	notifications,
	bottomAlertListEnabled,
	panelActions, // prop to include any additional action to the titleBar.
	children,
	disableNotificationPanel
}) => {
	const dispatch = useDispatch();

	const { toggleOpen } = actionCreators;

	const dir = useSelector((state) => getDir(state));
	const panelState = useSelector((state) => contextPanelState(state));
	const primaryOpen = panelState ? panelState.primaryOpen : null;
	const secondaryOpen =
		panelState && ($(".secondary-panel").length || $(".secondary-panelRTL").length)
			? panelState.secondaryOpen
			: null;
	const mapVisible = useSelector((state) => (state.mapState ? state.mapState.baseMap.visible : false));
	const showMobileToggle = mapVisible && $(window).width() <= 1023;
	const isDockControlOpen = primaryOpen || (showMobileToggle && secondaryOpen);
	const detailView = useSelector((state) => state.appState.persisted?.detailView);
	const bottomDockHeight = useSelector((state) => state.appState.persisted?.bottomDockHeight);
	const mapTools = useSelector((state) => state.mapState?.mapTools);
	const isShapeEditorOpen = mapTools?.type === "drawing";
	const offset = useSelector((state) => state?.appState?.mapRef?.offset ?? null);
	const appId = useSelector((state) => state.application.appId);

	const [dockControlWidth, setDockControlWidth] = useState(0);
	const [alertProfileWidth, setAlertProfileWidth] = useState(0);
	const transition = useRef("");
	const isResizingRef = useRef(false);

	const newHeightRef = useRef(50);
	const [newHeight, setNewHeight] = useState(50);

	const secondaryWidth = $(".alert-panel-slideout").length ? $(".alert-panel-slideout").outerWidth() / 2 : 0;

	useEffect(() => {
		const dockControl = isDockControlOpen
			? $(`.dock-control${dir === "rtl" ? "RTL" : ""}.open`)?.outerWidth()
			: $(`.dock-control${dir === "rtl" ? "RTL" : ""}-sidebar.open`)?.outerWidth();
		const secondaryPanel = !isDockControlOpen
			? $(`.secondary-panel${dir === "rtl" ? "RTL" : ""}.open`)?.outerWidth() + dockControl
			: $(`.secondary-panel${dir === "rtl" ? "RTL" : ""}.open`)?.outerWidth();
		const shapeEditor = isShapeEditorOpen ? $(`.shape-editor${dir === "rtl" ? "RTL" : ""}`)?.outerWidth() : 0;

		setDockControlWidth(shapeEditor || secondaryPanel || dockControl);
		setAlertProfileWidth(detailView ? $(".alert-panel-slideout")?.outerWidth() : 0);

		transition.current = isDockControlOpen ? "width 225ms ease-in-out -.5s" : "width 100ms ease-in-out -.8s";
	}, [isDockControlOpen, detailView, offset, secondaryOpen, isShapeEditorOpen]);

	useEffect(() => {
		if (detailView) {
			transition.current = "width 225ms ease-in-out";
		} else {
			transition.current = "unset";
		}

		if (secondaryWidth) dispatch(setMapOffset(detailView ? -secondaryWidth : secondaryWidth));
	}, [detailView]);

	useEffect(() => {
		if (!open && !disableNotificationPanel) dispatch(setLocalAppState("detailView", false));
	}, [open]);

	useEffect(() => {
		if (bottomDockHeight && newHeightRef.current !== bottomDockHeight) {
			newHeightRef.current = bottomDockHeight;
			setNewHeight(bottomDockHeight);
		}
	}, [bottomDockHeight]);

	const handleClose = () => {
		dispatch(setAppState({ detailView: false, selectedTab: null }));
		dispatch(toggleOpen());
	};

	const dialogTitle = (
		<div className={dir && dir == "rtl" ? "notification-titleRTL" : "notification-title"} style={{ height: 48 }}>
			{dockTitle}
		</div>
	);

	const styles = {
		panelActions: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			height: "100%"
		},
		dragger: {
			height: "5px",
			width: "100%",
			cursor: "n-resize",
			padding: "4px 0 0",
			position: "absolute",
			top: 0,
			left: 0,
			bottom: 0,
			zIndex: "100"
		}
	};

	const handleMousemove = (e) => {
		// calculate the new height of the bottom dock based on the mouse position
		if (e.buttons == 1 && isResizingRef.current) {
			document.body.style.userSelect = 'none';
			const windowHeight = document.body.offsetHeight;
			const dockHeight = windowHeight - e.clientY;
			const offsetBottom = (dockHeight / windowHeight) * 100;

			let minHeight = 20;
			let maxHeight = 75;

			if (offsetBottom > minHeight && offsetBottom < maxHeight) {
				newHeightRef.current = offsetBottom;
				setNewHeight(offsetBottom);
			}
		}
	};

	const handleMouseup = (e) => {
		isResizingRef.current = false;
		document.body.style.userSelect = '';
		// verify and save to the userAppState, if the bottomDockHeight has active changes.
		if (e.buttons == 0 && bottomDockHeight != newHeightRef.current) {
			dispatch(
				updatePersistedState(appId, "bottomDockHeight", {
					bottomDockHeight: newHeightRef.current
				})
			);
		}
	};

	// enable resizing only when mouse is focused on the resizer.
	const handleMouseDown = () => {
		isResizingRef.current = true;
	};

	useEffect(() => {
		if (open) {
			document.addEventListener("mousemove", handleMousemove);
			document.addEventListener("mouseup", handleMouseup);
		} else {
			document.removeEventListener("mousemove", handleMousemove);
			document.removeEventListener("mouseup", handleMouseup);
		}
	}, [open]);

	return (
		<Drawer
			open={open}
			anchor={"bottom"}
			PaperProps={{ className: "bottom-dock", style: { height: `${newHeight}%` } }}
			sx={{
				["& .MuiDrawer-paper"]: {
					backgroundColor: "transparent",
					position: "fixed!important",
					height: "calc(100vh - 48px)",
					top: "48px",
					overflow: " visible !important"
				}
			}}
			variant="persistent"
		>
			<div id="bottom-dock-resizer" style={styles.dragger} onMouseDown={handleMouseDown} />

			{open && (
				<div
					className="bottom-dock-slideout"
					style={{
						display: "flex",
						height: "100%"
					}}
				>
					<span
						style={{
							width: dockControlWidth,
							transition: isDockControlOpen
								? "width 225ms ease-in-out -.5s"
								: "width 100ms ease-in-out -.8s",
							background: "rgb(44, 43, 45)"
						}}
					></span>
					<span
						style={{
							width: `calc(100% - ${dockControlWidth + alertProfileWidth}px)`,
							background: "rgb(44, 43, 45)",
							transition: transition.current
						}}
					>
						<div onClick={() => dispatch(toggleOpen())} className="ad-toggle-mobile" />
						<div id="sidebar-inner-wrapper" className="cf">
							<ErrorBoundary>
								<div className={`ad-toggle-mobile bottom-dock-titleBar`}>
									{dialogTitle}
									<div style={styles.panelActions}>
										{panelActions}
										<FilterSettings customStyles={{ margin: "4px 0 0 0" }} />
										<SettingsMenu
											notifications={notifications}
											bottomAlertListEnabled={bottomAlertListEnabled}
											customStyles={{ margin: "0 10px" }}
										/>
										<a onClick={() => handleClose()}>
											<div className={dir && dir == "rtl" ? "close-ad-textRTL" : "close-ad-text"}>
												<Close sx={{ color: "#979798", fontSize: 22 }} />
											</div>
										</a>
									</div>
								</div>
								{children}
							</ErrorBoundary>
						</div>
					</span>
					<span
						style={{
							width: alertProfileWidth,
							transition: detailView ? "width 225ms ease-in-out" : "unset",
							background: "rgb(44, 43, 45)"
						}}
					></span>
				</div>
			)}
		</Drawer>
	);
};

BottomDock.propTypes = propTypes;
BottomDock.defaultProps = defaultProps;

export default BottomDock;
